import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  HouseSimple, Plus, Spinner, MapPin, ArrowRight, PencilSimple, Trash, Buildings,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPES = [
  { value: "apartman", label: "Apartman" },
  { value: "yurt", label: "Yurt" },
  { value: "residence", label: "Residence" },
  { value: "ofis", label: "Ofis Binasi" },
  { value: "site", label: "Site" },
];

export default function PropertiesPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProperty, setEditProperty] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: "", property_type: "apartman", address: "", city: "", billing_cycle_day: 1 });

  const fetchProperties = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/properties`);
      setProperties(res.data);
    } catch {
      toast.error("Binalar yuklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error("Bina adi zorunlu"); return; }
    try {
      if (editProperty) {
        await axios.put(`${API}/api/properties/${editProperty.id}`, form);
        toast.success("Bina guncellendi");
      } else {
        await axios.post(`${API}/api/properties`, form);
        toast.success("Bina olusturuldu");
      }
      setDialogOpen(false);
      setEditProperty(null);
      setForm({ name: "", property_type: "apartman", address: "", city: "", billing_cycle_day: 1 });
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Islem basarisiz");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/properties/${deleteTarget.id}`);
      toast.success("Bina silindi");
      setDeleteTarget(null);
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    }
  };

  const openEdit = (p) => {
    setEditProperty(p);
    setForm({
      name: p.name, property_type: p.property_type || "apartman",
      address: p.address || "", city: p.city || "",
      billing_cycle_day: p.billing_cycle_day || 1,
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditProperty(null);
    setForm({ name: "", property_type: "apartman", address: "", city: "", billing_cycle_day: 1 });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} className="animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bina Yonetimi</h1>
          <p className="text-sm text-slate-500 mt-1">{properties.length} bina kayitli</p>
        </div>
        <Button onClick={openNew} data-testid="create-property-btn">
          <Plus size={18} className="mr-2" /> Yeni Bina
        </Button>
      </div>

      {properties.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <HouseSimple size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Henuz bina eklenmemis.</p>
            <Button variant="outline" className="mt-4" onClick={openNew}>
              <Plus size={16} className="mr-2" /> Ilk Binayi Ekle
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {properties.map((p) => (
            <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`property-card-${p.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                      <HouseSimple size={22} className="text-indigo-600" weight="duotone" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{p.name}</CardTitle>
                      <Badge variant="secondary" className="text-xs mt-1">
                        {PROPERTY_TYPES.find(t => t.value === p.property_type)?.label || p.property_type}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-property-${p.id}`}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(p)} data-testid={`delete-property-${p.id}`}>
                      <Trash size={16} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {p.address && (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <MapPin size={14} /> {p.address}{p.city ? `, ${p.city}` : ""}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-50 rounded-lg p-2">
                    <div className="text-lg font-semibold text-slate-900">{p.total_units}</div>
                    <div className="text-xs text-slate-500">Unite</div>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-2">
                    <div className="text-lg font-semibold text-emerald-700">{p.occupied_units}</div>
                    <div className="text-xs text-emerald-600">Dolu</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <div className="text-lg font-semibold text-amber-700">{p.vacant_units}</div>
                    <div className="text-xs text-amber-600">Bos</div>
                  </div>
                </div>
                <Link
                  to={`/properties/${p.id}`}
                  className="flex items-center justify-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium pt-2"
                  data-testid={`property-detail-link-${p.id}`}
                >
                  Detay <ArrowRight size={14} />
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="property-dialog">
          <DialogHeader>
            <DialogTitle>{editProperty ? "Bina Duzenle" : "Yeni Bina Ekle"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Bina Adi *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                placeholder="Ornek: Gul Apartmani" data-testid="property-name-input" />
            </div>
            <div>
              <Label>Bina Tipi</Label>
              <Select value={form.property_type} onValueChange={v => setForm({...form, property_type: v})}>
                <SelectTrigger data-testid="property-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Adres</Label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                placeholder="Adres" data-testid="property-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sehir</Label>
                <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})}
                  placeholder="Sehir" data-testid="property-city-input" />
              </div>
              <div>
                <Label>Fatura Gunu (1-28)</Label>
                <Input type="number" min={1} max={28} value={form.billing_cycle_day}
                  onChange={e => setForm({...form, billing_cycle_day: parseInt(e.target.value) || 1})}
                  data-testid="property-billing-day-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Iptal</Button>
            <Button onClick={handleSubmit} data-testid="property-submit-btn">
              {editProperty ? "Guncelle" : "Olustur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Bina Silinecek</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" binasini silmek istediginizden emin misiniz?
              Bagli apartman varsa silinemez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Iptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-property-btn">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
